// src/components/products/ProductExclusions.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  fetchProduct,
  deleteExclusion,
  clearProductError,
} from '../../store/actions/productActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';
import Pagination from '../common/Pagination';
import SearchBar from '../common/SearchBar';

const ProductExclusions = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { selectedProduct: product, exclusions, loading, error } = useSelector(
    (state) => state.products
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [filteredExclusions, setFilteredExclusions] = useState([]);
  const [selectedExclusion, setSelectedExclusion] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  // Styles object (similar to Eligibility but adapted)
  const styles = {
    container: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      marginBottom: '2rem',
    },
    headerTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1rem',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    backButton: {
      padding: '0.5rem',
      backgroundColor: '#f3f4f6',
      border: 'none',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      color: '#374151',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.2s',
    },
    backButtonHover: {
      backgroundColor: '#e5e7eb',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    productInfo: {
      marginTop: '1rem',
      padding: '1rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    productName: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#111827',
    },
    productMeta: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginTop: '0.25rem',
    },
    viewProductButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      textDecoration: 'none',
      transition: 'all 0.2s',
    },
    controls: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem',
      flexWrap: 'wrap',
      gap: '1rem',
    },
    addButton: {
      padding: '0.75rem 1.5rem',
      backgroundColor: '#ef4444',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'background-color 0.2s',
    },
    addButtonHover: {
      backgroundColor: '#dc2626',
    },
    statsBar: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '1rem',
      marginBottom: '2rem',
      padding: '1.5rem',
      backgroundColor: '#f9fafb',
      borderRadius: '0.5rem',
    },
    statCard: {
      textAlign: 'center',
    },
    statValue: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '0.25rem',
    },
    statLabel: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    tableContainer: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    tableHeader: {
      backgroundColor: '#fee2e2',
      borderBottom: '1px solid #fecaca',
    },
    tableHeaderCell: {
      padding: '0.75rem 1.5rem',
      textAlign: 'left',
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#991b1b',
      borderBottom: '1px solid #fecaca',
    },
    tableHeaderCellRight: {
      padding: '0.75rem 1.5rem',
      textAlign: 'right',
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#991b1b',
      borderBottom: '1px solid #fecaca',
    },
    tableRow: {
      borderBottom: '1px solid #fee2e2',
      transition: 'background-color 0.2s',
    },
    tableRowHover: {
      backgroundColor: '#fef2f2',
    },
    tableCell: {
      padding: '1rem 1.5rem',
      fontSize: '0.875rem',
      color: '#111827',
    },
    tableCellRight: {
      padding: '1rem 1.5rem',
      fontSize: '0.875rem',
      color: '#111827',
      textAlign: 'right',
    },
    exclusionText: {
      fontSize: '0.95rem',
      color: '#991b1b',
      lineHeight: '1.5',
    },
    badge: {
      display: 'inline-flex',
      padding: '0.25rem 0.5rem',
      fontSize: '0.75rem',
      fontWeight: 500,
      borderRadius: '9999px',
      backgroundColor: '#fee2e2',
      color: '#991b1b',
    },
    orderBadge: {
      backgroundColor: '#fef3c7',
      color: '#92400e',
    },
    actionsContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: '0.5rem',
    },
    actionButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.25rem',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      color: '#6b7280',
      transition: 'color 0.2s',
      borderRadius: '0.375rem',
    },
    editButton: {
      color: '#4f46e5',
    },
    editButtonHover: {
      color: '#4338ca',
    },
    deleteButton: {
      color: '#dc2626',
    },
    deleteButtonHover: {
      color: '#b91c1c',
    },
    icon: {
      width: '1.25rem',
      height: '1.25rem',
    },
    emptyState: {
      textAlign: 'center',
      padding: '4rem 2rem',
      backgroundColor: '#fef2f2',
      borderRadius: '0.5rem',
      border: '2px dashed #fecaca',
    },
    emptyIcon: {
      width: '4rem',
      height: '4rem',
      marginBottom: '1rem',
      color: '#ef4444',
    },
    emptyTitle: {
      fontSize: '1.125rem',
      fontWeight: 500,
      color: '#991b1b',
      marginBottom: '0.5rem',
    },
    emptyText: {
      fontSize: '0.875rem',
      color: '#b91c1c',
      marginBottom: '1.5rem',
    },
    footer: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: '2rem',
    },
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchProduct(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (exclusions) {
      filterAndPaginateExclusions();
    }
  }, [exclusions, searchTerm, page]);

  const filterAndPaginateExclusions = () => {
    // Filter out any undefined or null items first
    const validExclusions = (exclusions || []).filter(e => e != null);
    
    let filtered = [...validExclusions];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(e => 
        e?.exclusion?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort by order
    filtered.sort((a, b) => (a?.order || 0) - (b?.order || 0));

    // Calculate pagination
    const total = filtered.length;
    const totalPages = Math.ceil(total / limit);
    const start = (page - 1) * limit;
    const paginatedExclusions = filtered.slice(start, start + limit);

    setFilteredExclusions(paginatedExclusions);
    setPagination({
      page,
      limit,
      total,
      totalPages,
    });
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleDeleteClick = (exclusion) => {
    setSelectedExclusion(exclusion);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteExclusion(id, selectedExclusion.id));
      setShowDeleteConfirm(false);
      setSelectedExclusion(null);
      dispatch(fetchProduct(id));
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  if (loading && !product) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <div style={styles.headerLeft}>
            <button
              onClick={() => navigate(`/admin/products/${id}/details`)}
              style={styles.backButton}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = styles.backButtonHover.backgroundColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = styles.backButton.backgroundColor;
              }}
            >
              <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <h1 style={styles.title}>Exclusions</h1>
          </div>
        </div>

        {product && (
          <div style={styles.productInfo}>
            <div>
              <div style={styles.productName}>{product.name}</div>
              <div style={styles.productMeta}>
                {product.shortName && `${product.shortName} • `}
                {exclusions?.length || 0} exclusion(s)
              </div>
            </div>
            <Link
              to={`/admin/products/${id}/details`}
              style={styles.viewProductButton}
            >
              Voir le produit
            </Link>
          </div>
        )}
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearProductError())}
        />
      )}

      {/* Stats */}
      <div style={styles.statsBar}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{exclusions?.length || 0}</div>
          <div style={styles.statLabel}>Total exclusions</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>
            {exclusions && exclusions.length > 0
              ? Math.max(...exclusions.map(e => e?.order || 0), 0)
              : 0}
          </div>
          <div style={styles.statLabel}>Ordre max</div>
        </div>
      </div>

      {/* Controls */}
      <div style={styles.controls}>
        <div style={{ width: '300px' }}>
          <SearchBar
            onSearch={handleSearch}
            placeholder="Rechercher une exclusion..."
          />
        </div>
        <Link
          to={`/admin/products/${id}/exclusions/add`}
          style={styles.addButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = styles.addButtonHover.backgroundColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = styles.addButton.backgroundColor;
          }}
        >
          <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Nouvelle exclusion
        </Link>
      </div>

      {/* Exclusions Table */}
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead style={styles.tableHeader}>
            <tr>
              <th style={styles.tableHeaderCell}>Exclusion</th>
              <th style={styles.tableHeaderCell}>Ordre</th>
              <th style={styles.tableHeaderCellRight}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredExclusions.map((exclusion) => (
              exclusion && (
                <tr 
                  key={exclusion.id} 
                  style={styles.tableRow}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = styles.tableRowHover.backgroundColor;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <td style={styles.tableCell}>
                    <div style={styles.exclusionText}>{exclusion.exclusion}</div>
                  </td>
                  <td style={styles.tableCell}>
                    <span style={{ ...styles.badge, ...styles.orderBadge }}>
                      #{exclusion.order || 0}
                    </span>
                  </td>
                  <td style={styles.tableCellRight}>
                    <div style={styles.actionsContainer}>
                      <Link
                        to={`/admin/products/${id}/exclusions/${exclusion.id}/edit`}
                        style={{
                          ...styles.actionButton,
                          ...styles.editButton,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = styles.editButtonHover.color;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = styles.editButton.color;
                        }}
                        title="Modifier"
                      >
                        <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </Link>
                      <button
                        onClick={() => handleDeleteClick(exclusion)}
                        style={{
                          ...styles.actionButton,
                          ...styles.deleteButton,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = styles.deleteButtonHover.color;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = styles.deleteButton.color;
                        }}
                        title="Supprimer"
                      >
                        <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              )
            ))}
          </tbody>
        </table>

        {filteredExclusions.length === 0 && (
          <div style={styles.emptyState}>
            <svg style={styles.emptyIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
            <div style={styles.emptyTitle}>
              {searchTerm ? 'Aucune exclusion trouvée' : 'Aucune exclusion'}
            </div>
            <div style={styles.emptyText}>
              {searchTerm 
                ? 'Essayez de modifier votre recherche'
                : 'Commencez par ajouter une exclusion à ce produit'}
            </div>
            {!searchTerm && (
              <Link
                to={`/admin/products/${id}/exclusions/add`}
                style={styles.addButton}
              >
                Ajouter une exclusion
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div style={styles.footer}>
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setSelectedExclusion(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Supprimer l'exclusion"
        message={`Êtes-vous sûr de vouloir supprimer cette exclusion ?`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />
    </div>
  );
};

export default ProductExclusions;